import { Button, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { monthlyReportApi } from "../api/client";
import {
  MonthlyReportTour,
  monthlyReportFormCreateTourSteps,
  monthlyReportFormEditTourSteps,
  useMonthlyReportTour,
} from "../tour";
import { useMonthlyReportById } from "../api/queries";
import type { MonthlyReportDetail } from "@/modules/management/monthly-report/domain/types";
import type { MonthlyReportFormValues } from "../ui/MonthlyReportForm";
import { MonthlyReportForm } from "../ui/MonthlyReportForm";
import { ArrowLeft, HelpCircle } from "lucide-react";

const emptyFormValues: MonthlyReportFormValues = {
  periodYear: new Date().getFullYear(),
  periodMonth: new Date().getMonth() + 1,
  departmentId: "",
  title: "",
  fileKey: "",
  documentUrl: "",
};

function detailToFormValues(
  detail: MonthlyReportDetail,
): MonthlyReportFormValues {
  return {
    periodYear: detail.periodYear,
    periodMonth: detail.periodMonth,
    departmentId: detail.departmentId ?? "",
    title: detail.title ?? "",
    fileKey: detail.fileKey ?? "",
    documentUrl: detail.documentUrl ?? "",
  };
}

type MonthlyReportFormPageProps = {
  mode: "create" | "edit";
};

export function MonthlyReportFormPage({ mode }: MonthlyReportFormPageProps) {
  const params = useParams({ strict: false }) as { id?: string };
  const reportQuery = useMonthlyReportById(
    mode === "edit" ? params.id : undefined,
  );

  const initialValues = useMemo((): MonthlyReportFormValues => {
    if (mode === "edit" && reportQuery.data) {
      return detailToFormValues(reportQuery.data);
    }
    return emptyFormValues;
  }, [mode, reportQuery.data]);

  const nav = useNavigate({ from: "/app/management/monthly-reports" });
  const queryClient = useQueryClient();
  const formRef = useRef<{ submit: () => void } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = () => {
    nav({
      to: "/app/management/monthly-reports",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        periodYear: undefined,
        periodMonth: undefined,
        departmentId: undefined,
        search: undefined,
      },
    });
  };

  const tour = useMonthlyReportTour(
    mode === "create" ? "formCreate" : "formEdit",
  );
  const formSteps =
    mode === "create"
      ? monthlyReportFormCreateTourSteps
      : monthlyReportFormEditTourSteps;
  const backTourId =
    mode === "create"
      ? "monthly-report-form-create-back"
      : "monthly-report-form-edit-back";
  const formTourId =
    mode === "create"
      ? "monthly-report-form-create-form"
      : "monthly-report-form-edit-form";

  const handleSubmit = async (values: MonthlyReportFormValues) => {
    setIsSubmitting(true);
    const body = {
      periodYear: values.periodYear,
      periodMonth: values.periodMonth,
      departmentId: values.departmentId?.trim() || null,
      title: values.title?.trim() || null,
      fileKey: values.fileKey?.trim() || null,
      documentUrl: values.documentUrl?.trim() || null,
    };

    try {
      if (mode === "create") {
        const { id } = await monthlyReportApi.create(body);
        toast.success("ສ້າງລາຍງານສຳເລັດ");
        nav({
          to: "/app/management/monthly-reports/$id",
          params: { id },
        });
      } else if (params.id) {
        await monthlyReportApi.update(params.id, body);
        toast.success("ບັນທຶກການປ່ຽນແປງສຳເລັດ");
        queryClient.invalidateQueries({
          queryKey: ["management", "monthly-reports"],
        });
        nav({
          to: "/app/management/monthly-reports/$id",
          params: { id: params.id },
        });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === "edit" && reportQuery.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (mode === "edit" && reportQuery.isError) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <p className="text-destructive">ບໍ່ພົບລາຍງານ ຫຼື ເກີດຂໍ້ຜິດພາດ</p>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() =>
                nav({
                  to: "/app/management/monthly-reports",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    periodYear: undefined,
                    periodMonth: undefined,
                    departmentId: undefined,
                    search: undefined,
                  },
                })
              }
            >
              ກັບໄປລາຍການ
            </button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <MonthlyReportTour
          steps={formSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">
                  {mode === "create"
                    ? "ສ້າງລາຍງານປະຈໍາເດືອນ"
                    : "ແກ້ໄຂລາຍງານປະຈໍາເດືອນ"}
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                {mode === "create"
                  ? "ເລືອກເດືອນ ປີ ພະແນກ ແລະ ອັບໂຫຼດເອກະສານລາຍງານ"
                  : "ແກ້ໄຂຂໍ້ມູນລາຍງານປະຈໍາເດືອນ"}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            data-tourid={backTourId}
            onClick={() =>
              nav({
                to: "/app/management/monthly-reports",
                search: {
                  offset: 0,
                  limit: 20,
                  sort: undefined,
                  periodYear: undefined,
                  periodMonth: undefined,
                  departmentId: undefined,
                  search: undefined,
                },
              })
            }
          >
            <ArrowLeft className="h-4 w-4" /> ກັບໄປລາຍການ
          </Button>
        </div>
        <div className="mx-auto max-w-3xl space-y-6" data-tourid={formTourId}>
          <MonthlyReportForm
            defaultValues={initialValues}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            mode={mode}
            formRef={formRef}
          />
        </div>
      </Main>
    </>
  );
}
